// eslint-disable-next-line fsd/layer-imports
import { useProfile } from '@/entities/profile'

import { useActivityTracking } from './useActivityTracking'
import { useAmplitude } from './useAmplitude'

export const usePageTracking = () => {
  const { profile } = storeToRefs(useProfile())
  const { track } = useAmplitude()
  const { updateActivity } = useActivityTracking()

  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('amplitude_session_id')
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      sessionStorage.setItem('amplitude_session_id', sessionId)
    }
    return sessionId
  }

  const getPageName = (path: string): string => {
    const pathMappings: Record<string, string> = {
      '/': 'home',
      '/news': 'news',
      '/events': 'events',
      '/allocate-coins': 'allocate_coins',
      '/faq': 'faq',
      '/feedback': 'feedback',
      '/shop': 'shop',
      '/my-orders': 'my_orders',
      '/metrics': 'metrics',
      '/profiles': 'profiles',
      '/profile': 'profile',
    }

    if (pathMappings[path]) {
      return pathMappings[path]
    }

    if (path.startsWith('/news')) {
      return 'news'
    }
    if (path.startsWith('/events')) {
      return 'events'
    }
    if (path.startsWith('/balance')) {
      return 'balance'
    }
    if (path.startsWith('/faq')) {
      return 'faq'
    }
    if (path.startsWith('/shop')) {
      return 'shop'
    }
    if (path.startsWith('/my-orders')) {
      return 'my_orders'
    }
    if (path.startsWith('/profiles')) {
      return 'profiles'
    }
    if (path.startsWith('/profile')) {
      return 'profile'
    }
    if (path.startsWith('/admin')) {
      return 'admin'
    }

    return path.split('/')[1] || 'unknown'
  }

  const trackPageView = (path: string) => {
    if (!profile.value) {
      return
    }

    const pageName = getPageName(path)

    const trackablePages = [
      'news',
      'events',
      'balance',
      'allocate_coins',
      'faq',
      'feedback',
      'shop',
      'my_orders',
      'metrics',
      'profiles',
      'profile',
    ]

    if (trackablePages.includes(pageName)) {
      track('page_view', {
        user_id: profile.value.id,
        page_name: pageName,
        session_id: getSessionId(),
        timestamp: Date.now(),
        full_path: path,
        user_role: profile.value.role,
        department: profile.value.department?.name || 'unassigned',
      })

      updateActivity(`page_view_${pageName}`)
    }
  }

  const trackWidgetView = (widgetName: string) => {
    if (!profile.value) {
      return
    }

    track('page_view', {
      user_id: profile.value.id,
      page_name: `widget_${widgetName}`,
      session_id: getSessionId(),
      timestamp: Date.now(),
      full_path: '/',
      user_role: profile.value.role,
      department: profile.value.department?.name || 'unassigned',
    })

    updateActivity(`widget_${widgetName}`)
  }

  return {
    trackPageView,
    trackWidgetView,
    getPageName,
    getSessionId,
  }
}
